import Vue from 'vue';
import _ from 'lodash';
import { has } from '../../utils.js';

/**
 * A store for application context menu - non persisting store
 */
const state = {
  items: [], // properties: id, name, icon, commands: { click, isActive, isEnabled }, submenu: { component, props }, addon: { component, props }
  title: null,
  calcPosition: () => {},
  position: { left: 0, top: 0 },
  openSubmenu: null,
  calcSubmenuPosition: () => {},
  submenuPosition: { left: 0, top: 0 },
  onDeleteMenus: [],
};

const getters = {
  /**
   * Returns whether the menu is empty (has no items)
   * @return  {Boolean}     Whether the menu has no items
   */
  getIsEmpty(state) {
    return state.items.length === 0;
  },

  /**
   * Returns the menu items
   * @return  {Array}       The items
   */
  getItems(state) {
    return state.items;
  },

  /**
   * Returns a menu item
   * @param   {String}  id    The item id
   * @return  {Object}        The item
   */
  getItem(state) {
    return id => {
      return state.items.find(item => item.id === id);
    };
  },

  /**
   * Returns the menu title
   * @return {string}
   */
  getTitle(state) {
    return state.title;
  },

  /**
   * Returns whether a menu item is active
   * @param   {String}  id    The item id
   * @return  {Boolean}       Whether it is active
   */
  getIsActive(state, getters) {
    return id => {
      const item = getters.getItem(id);
      if (has(item.commands, 'isActive') && item.commands.isActive) {
        return item.commands.isActive();
      }
      return false;
    };
  },

  /**
   * Returns whether a menu item is enabled
   * @param   {String}  id    The item id
   * @return  {Boolean}       Whether it is enabled
   */
  getIsEnabled(state, getters) {
    return id => {
      const item = getters.getItem(id);
      if (has(item.commands, 'isEnabled') && item.commands.isEnabled) {
        return item.commands.isEnabled();
      }
      return false;
    };
  },

  /**
   * Return the next available id for item creation
   * @return  {String}   The next available id
   */
  getNextId(state) {
    const max = Math.max(
      ...state.items.map(item => parseInt(item.id.substring(5), 10) || 0),
      0
    );
    return `item_${max + 1}`;
  },

  /**
   * Returns the menu position
   * @return    {Object}      The position { left, top }
   */
  getPosition(state) {
    return state.position;
  },

  /**
   * Returns whether the submenu is open
   * @return  {Boolean}       Whether the subemu is open
   */
  getOpenSubmenu(state) {
    return state.openSubmenu;
  },

  /**
   * Returns the submenu of a menu item
   * @param   {String} id   The item id
   * @return  {Object}      The submenu
   */
  getSubmenu(state, getters) {
    return id => {
      const item = getters.getItem(id);
      if (has(item, 'submenu')) {
        return item.submenu;
      }
      return null;
    };
  },

  /**
   * Returns the add on of a menu item
   * @param   {String}  id  The item id
   * @return  {Object}      The addon
   */
  getAddon(state, getters) {
    return id => {
      const item = getters.getItem(id);
      if (has(item, 'addon')) {
        console.log('context-menu: Returning the following addon:', item.addon);
        return item.addon;
      }
      return null;
    };
  },

  /**
   * Returns the position of the submenu
   * @return   {Object}    The submenu position
   */
  getSubmenuPosition(state) {
    return state.submenuPosition;
  },
};

const actions = {
  /**
   * Create a menu item
   * @param {String}    name            The item name (a unique identifier for menu item)
   * @param {String}    icon            The icon name
   * @param {Object}    commands        The commands to be executed { doAction, isActive, isEnabled }
   * @param {Object}    submenu         The submenu this item opens
   * @param {Object}    addon           The addon this item contains
   * @param {boolean}   upgrade         This item requires upgrading
   * @param {boolean}   showName        Whether to display the name
   * @return {String}                   The item id
   */
  createItem(
    context,
    { name, icon, commands, submenu, addon, upgrade = false, showName = true }
  ) {
    const id = context.getters.getNextId;
    // First check if an item with that name already exists
    const index = context.state.items.findIndex(button => button.name === name);
    if (index === -1) {
      context.commit('createItem', {
        id,
        name,
        icon,
        commands,
        submenu,
        addon,
        upgrade,
        showName,
      });
    }
    return Promise.resolve(id);
  },

  /**
   * Remove a menu item
   * @param {String}  name  The item name
   */
  deleteItem(context, { name }) {
    const index = context.state.items.findIndex(item => item.name === name);
    if (index !== -1) {
      context.commit('deleteItem', { index });
    }
  },

  /**
   * Create a menu title
   * @param {string} title  The menu title
   */
  createTitle(context, { title }) {
    context.commit('createTitle', { title });
  },

  /**
   * Remove the menu
   */
  deleteMenu(context) {
    context.commit('deleteMenu');
    for (let i = context.state.onDeleteMenus.length - 1; i >= 0; i -= 1) {
      const onDeleteMenu = context.state.onDeleteMenus[i];
      onDeleteMenu();
    }
    context.commit('clearOnDeleteMenus');
  },

  /**
   * Perform the click action on an item
   */
  doAction(context, { id }) {
    const item = context.getters.getItem(id);
    if (has(item.commands, 'doAction') && item.commands.doAction) {
      item.commands.doAction();
    }
  },

  /**
   * Set the calculate position callback
   * @param {Function} anchor The calculation callback
   */
  setCalcPosition(context, { calcPosition }) {
    context.commit('setCalcPosition', { calcPosition });
    context.commit('reposition');
  },

  /**
   * Open the submenu of a specific menu item
   * @param {String}    id   The item id
   * @param {Function}  calcSubmenuPosition The submenu function
   */
  openSubmenu(context, { id, anchor }) {
    context.commit('openSubmenu', { id, anchor });
    context.dispatch('setSubmenuPosition');
  },

  /**
   * Set the position of the submenu
   */
  setSubmenuPosition(context) {
    if (!context.state.submenuAnchor) {
      return;
    }

    const rect = context.state.submenuAnchor();

    if (rect != null) {
      const position = {
        left: rect.left,
        top: rect.bottom + 10,
      };
      context.commit('setSubmenuPosition', { position });
    }
  },

  /**
   * Recalculate the anchor rectangle
   */
  recalcAnchorRect(context) {
    context.commit('recalcAnchorRect');
  },

  /**
   * Set the callback for onDelete
   */
  setOnDeleteMenu(context, { onDeleteMenu }) {
    context.commit('pushOnDeleteMenu', { onDeleteMenu });
  },
};

const mutations = {
  /**
   * Clear the menu
   */
  deleteMenu(state) {
    state.items = [];
    state.title = null;
  },

  /**
   * Create a menu item
   * @param {String} id
   * @param {String} name
   * @param {String} icon
   * @param {Object} commands
   * @param {Object} submenu
   * @param {boolean} upgrade
   */
  createItem(
    state,
    { id, name, icon, commands, submenu, addon, upgrade, showName }
  ) {
    state.items.push({
      id,
      name,
      icon,
      commands,
      submenu,
      addon,
      upgrade,
      showName,
    });
  },

  /**
   * Remove a menu item at a specific index
   * @param {Number} index
   */
  deleteItem(state, { index }) {
    state.items.splice(index, 1);
  },

  /**
   * Create a menu title
   * @param {string} title  The menu title
   */
  createTitle(context, { title }) {
    state.title = title;
  },

  /**
   * Sets the callback that calculates the menu position
   * @param {Function} calcPosition The callback that calculates the position
   */
  setCalcPosition(state, { calcPosition }) {
    state.calcPosition = calcPosition;
  },

  pushOnDeleteMenu(state, { onDeleteMenu }) {
    state.onDeleteMenus.push(onDeleteMenu);
  },

  clearOnDeleteMenus(state) {
    state.onDeleteMenus = [];
  },

  reposition(state) {
    if (state.calcPosition) {
      state.position = state.calcPosition();
    }
  },

  openSubmenu(state, { id, anchor }) {
    state.openSubmenu = id;
    state.submenuAnchor = anchor;
  },

  setSubmenuPosition(state, { position }) {
    state.submenuPosition = position;
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
